import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@material-ui/core";
import { useDebounce } from "use-debounce";
import { trackPromise } from "react-promise-tracker";

import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import { AuthContext, UIContext } from "../../../../contexts";
import { PaginationQuery } from "../../../../@types/pagination.types";
import getLimit from "../../../../utils/pagination/getLimit";
import ROLES from "../../../../constants/roles";
import { api } from "../../../../utils/api";
import useRouteParam from "../../../../hooks/useRouteParam";
import useQuerySearch from "../../../../hooks/querySearch/useQuerySearch";
import useScopedRoute from "../../../../hooks/useScopedRoute";
import { DataGrid, DataGridColumn, GridAction, Page, PageHeader, StackedCell } from "../../../shared";

type CustomerRow = {
	_id: string;
	customer_id?: string;
	first_name?: string;
	last_name?: string;
	gender?: string;
	phone?: string;
	age_range?: string;
	group_number?: number;
};

/** Filterable fields, sent to the API as case-insensitive regex matches. */
const FILTER_FIELDS = ["first_name", "last_name", "phone"] as const;

const GroupCustomers = (): JSX.Element => {
	const { user } = useContext(AuthContext);
	const { setCustomerUI, setDashboardHeader } = useContext(UIContext);
	const navigate = useNavigate();
	const { buildRoute } = useScopedRoute();

	const group_number = useRouteParam("gn");
	const store_number = useRouteParam("sn");
	const page = useQuerySearch("page");

	const [limit, setLimit] = useState<number | null>(null);
	const [currentPage, setCurrentPage] = useState<number>(Number(page) || 1);
	const [pageCount, setPageCount] = useState<number | undefined>(undefined);
	const [customerList, setCustomerList] = useState<CustomerRow[]>([]);
	const [loading, setLoading] = useState(true);

	const [filters, setFilters] = useState<Record<string, string>>({});
	// Typing in the filter row should not fire a request per keystroke.
	const [debouncedFilters] = useDebounce(filters, 400);

	const xl = useMediaQuery("(min-width:1600px)");
	const lg = useMediaQuery("(min-width:1200px)");
	const md = useMediaQuery("(min-width:800px)");
	const sm = useMediaQuery("(min-width:600px)");
	const xs = useMediaQuery("(min-width:400px)");
	const _limit = getLimit(xl, lg, md, sm, xs);

	const handleViewCustomerDetails = useCallback(
		(row: CustomerRow) => {
			setCustomerUI(row as never);
			navigate(buildRoute("registered-customer-scan-details", { qs: row._id }));
		},
		[navigate, buildRoute, setCustomerUI]
	);

	const handlePageChange = useCallback(
		(nextPage: number) => {
			setCurrentPage(nextPage);
			navigate(buildRoute("customers", { page: nextPage }));
		},
		[navigate, buildRoute]
	);

	const handleFilterChange = useCallback((key: string, value: string) => {
		setFilters((current) => ({ ...current, [key]: value }));
		// Any new filter puts us back on the first page of results.
		setCurrentPage(1);
	}, []);

	const getPaginatedCustomerList = useCallback(async () => {
		if (typeof limit !== "number") {
			return;
		}
		const paginationQuery: PaginationQuery = { limit, page: currentPage };
		const query: Record<string, unknown> = { ...paginationQuery, group_number };
		if (store_number) {
			query.store_number = store_number;
		}
		FILTER_FIELDS.forEach((field) => {
			const value = debouncedFilters[field];
			if (value) {
				query[field] = { $regex: value, $options: "i" };
			}
		});

		setLoading(true);
		try {
			const response = await trackPromise(api.customer.getPaginatedCustomerList(query));
			if (response) {
				setCustomerList(response.customers ?? []);
				setPageCount(response.totalPages);
			}
		} finally {
			setLoading(false);
		}
	}, [limit, currentPage, group_number, store_number, debouncedFilters]);

	useEffect(() => {
		if (_limit >= 0) {
			setLimit(_limit);
		}
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.customers);
	}, [_limit, setDashboardHeader]);

	useEffect(() => {
		getPaginatedCustomerList();
	}, [getPaginatedCustomerList]);

	useEffect(() => {
		if (page) {
			setCurrentPage(Number(page));
		}
	}, [page]);

	const columns = useMemo<DataGridColumn<CustomerRow>[]>(() => {
		const list: DataGridColumn<CustomerRow>[] = [];

		if (user && user.role === ROLES.super_admin) {
			list.push({ key: "group_number", header: "Group", width: "7rem" });
		}

		list.push(
			{ key: "customer_id", header: "Customer ID", width: "10rem" },
			{
				key: "first_name",
				header: "Name",
				filterable: true,
				filterPlaceholder: "Name",
				render: (row) => (
					<StackedCell
						primary={[row.first_name, row.last_name].filter(Boolean).join(" ") || "—"}
						secondary={row.age_range ? `Age ${row.age_range}` : undefined}
					/>
				)
			},
			{ key: "gender", header: "Gender", width: "7rem" },
			{ key: "phone", header: "Phone", filterable: true, filterPlaceholder: "Phone", width: "10rem" },
			{
				key: "action",
				header: "Action",
				width: "10rem",
				stopRowClick: true,
				render: (row) => (
					<GridAction tone="primary" onClick={() => handleViewCustomerDetails(row)}>
						See details
					</GridAction>
				)
			}
		);

		return list;
	}, [user, handleViewCustomerDetails]);

	return (
		<Page>
			<PageHeader title="Customers" subtitle="Registered patients for this group" />
			<DataGrid
				rows={customerList}
				columns={columns}
				getRowId={(row, index) => row._id ?? String(index)}
				loading={loading}
				emptyMessage="No customers match this search."
				onRowClick={handleViewCustomerDetails}
				filters={filters}
				onFilterChange={handleFilterChange}
				pagination={{ currentPage, pageCount, onPageChange: handlePageChange }}
			/>
		</Page>
	);
};

export default GroupCustomers;
